/**
 * @file Drawer de notifications in-app accessible depuis la barre de navigation.
 * @module common.layouts._common.notifications-popover.NotificationsPopover
 *
 * Affiche les notifications regroupées par période (Aujourd'hui, Hier, Cette semaine, Plus ancien)
 * avec pagination progressive. Le badge de l'icône en en-tête reflète le compteur non lus
 * (rafraîchi toutes les 30 s via useUnreadCount).
 */
import {m} from 'framer-motion';
import {useState, useCallback, useMemo} from 'react';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import {useBoolean} from 'packages/common/hooks/useBoolean';
import {useActiveCompany} from 'packages/common/hooks/session';
import {useNotifications, useUnreadCount, useMarkAllAsRead, useToggleReadStatus} from 'packages/common/hooks/api/app/Notifications';
import Label from 'packages/common/components/label';
import Iconify from 'packages/common/components/iconify';
import Scrollbar from 'packages/common/components/scrollbar';
import {varHover} from 'packages/common/components/animate';
import NotificationItem from './notification-item';

const TABS = [
  {value: 'all', label: 'Toutes'},
  {value: 'unread', label: 'Non lues'},
];

/**
 * Regroupe les notifications par période temporelle
 *
 * @param {Array} notifications - Liste de notifications triées par date décroissante
 * @return {Array<{label: string, items: Array}>} Groupes avec label et items
 */
function groupByDate(notifications) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday - 86400000);
  const startOfWeek = new Date(startOfToday - 7 * 86400000);

  const buckets = {
    'Aujourd\'hui': [],
    'Hier': [],
    '7 derniers jours': [],
    'Plus ancien': [],
  };

  notifications.forEach((n) => {
    const d = new Date(n.createdAt);
    if (d >= startOfToday) buckets['Aujourd\'hui'].push(n);
    else if (d >= startOfYesterday) buckets['Hier'].push(n);
    else if (d >= startOfWeek) buckets['7 derniers jours'].push(n);
    else buckets['Plus ancien'].push(n);
  });

  return Object.entries(buckets)
      .filter(([, items]) => items.length > 0)
      .map(([label, items]) => ({label, items}));
}

export default function NotificationsPopover() {
  const drawer = useBoolean();
  const {companyId} = useActiveCompany();

  const [currentTab, setCurrentTab] = useState('all');

  const handleChangeTab = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  const {notifications, isLoading, isLoadingMore, hasMore, loadMore, mutate: mutateNotifications} = useNotifications(companyId);
  const {unreadCount, mutate: mutateUnreadCount} = useUnreadCount(companyId);
  const {markAllAsRead, isLoading: isMarkingRead} = useMarkAllAsRead();
  const {toggleReadStatus} = useToggleReadStatus(mutateNotifications, mutateUnreadCount);

  const filteredNotifications = useMemo(() => {
    if (currentTab === 'unread') {
      return notifications.filter((n) => !n.isRead);
    }
    return notifications;
  }, [notifications, currentTab]);

  const groups = useMemo(() => groupByDate(filteredNotifications), [filteredNotifications]);

  const handleMarkAllAsRead = async () => {
    await markAllAsRead(companyId);
    mutateNotifications();
    mutateUnreadCount();
  };

  const renderHead = (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        py: 2,
        pl: 2.5,
        pr: 1.5,
        minHeight: 64,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{flexGrow: 1}}>
        <Typography variant="h6" sx={{fontWeight: 700}}>
          Notifications
        </Typography>
        {unreadCount > 0 && (
          <Label color="error" variant="filled" sx={{height: 20, minWidth: 20, px: 0.75, fontSize: '0.7rem'}}>
            {unreadCount}
          </Label>
        )}
      </Stack>

      <IconButton size="small" onClick={drawer.onFalse} sx={{'color': 'text.secondary', '&:hover': {bgcolor: 'action.hover'}}}>
        <Iconify icon="mingcute:close-line" width={20} />
      </IconButton>
    </Stack>
  );

  const renderTabs = (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        pl: 2.5,
        pr: 1,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Tabs
        value={currentTab}
        onChange={handleChangeTab}
        sx={{
          'flexGrow': 1,
          '& .MuiTab-root': {
            minHeight: 48,
            py: 0,
            fontSize: '0.875rem',
          },
        }}
      >
        {TABS.map((tab) => (
          <Tab
            key={tab.value}
            iconPosition="end"
            value={tab.value}
            label={tab.label}
            icon={
              tab.value === 'unread' && unreadCount > 0 ? (
                <Label
                  variant={currentTab === 'unread' ? 'filled' : 'soft'}
                  color="info"
                  sx={{height: 20, minWidth: 20, px: 0.75, fontSize: '0.7rem'}}
                >
                  {unreadCount}
                </Label>
              ) : undefined
            }
            sx={{'&:not(:last-of-type)': {mr: 2}}}
          />
        ))}
      </Tabs>

      {unreadCount > 0 && (
        <Tooltip title="Tout marquer comme lu">
          <IconButton
            size="small"
            onClick={handleMarkAllAsRead}
            disabled={isMarkingRead}
            sx={{'color': 'text.secondary', '&:hover': {color: 'primary.main', bgcolor: 'action.hover'}}}
          >
            <Iconify icon="eva:done-all-fill" width={20} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  const renderEmpty = (
    <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{py: 12}}>
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Iconify icon="solar:bell-off-bold" width={32} sx={{color: 'text.disabled'}} />
      </Box>
      <Stack alignItems="center" spacing={0.5}>
        <Typography variant="subtitle2" color="text.secondary">
          {currentTab === 'unread' ? 'Tout est lu !' : 'Aucune notification'}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {currentTab === 'unread' ? 'Vous êtes à jour.' : 'Les alertes apparaîtront ici.'}
        </Typography>
      </Stack>
    </Stack>
  );

  const renderList = (
    <Scrollbar sx={{flexGrow: 1}}>
      {isLoading ? (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
          <CircularProgress size={28} />
        </Box>
      ) : filteredNotifications.length === 0 ? (
        renderEmpty
      ) : (
        <List disablePadding>
          {groups.map((group) => (
            <Box key={group.label}>
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  px: 2.5,
                  py: 1.25,
                  mt: 1,
                  bgcolor: 'action.hover',
                  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{color: 'text.disabled', fontSize: '0.65rem', letterSpacing: 0.8, flexGrow: 1}}
                >
                  {group.label}
                </Typography>
                <Typography variant="caption" sx={{color: 'text.disabled', fontSize: '0.68rem'}}>
                  {group.items.length}
                </Typography>
              </Stack>

              {group.items.map((notification) => (
                <NotificationItem
                  key={notification._id}
                  notification={notification}
                  onClose={drawer.onFalse}
                  onToggleRead={(notificationId, currentIsRead) =>
                    toggleReadStatus(companyId, notificationId, currentIsRead)
                  }
                />
              ))}
            </Box>
          ))}

          {/* Bouton de chargement progressif — visible uniquement sur l'onglet "toutes" */}
          {currentTab === 'all' && (hasMore || isLoadingMore) && (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 2, borderTop: (theme) => `1px solid ${theme.palette.divider}`}}>
              {isLoadingMore ? (
                <CircularProgress size={22} />
              ) : (
                <Button size="small" onClick={loadMore} sx={{'color': 'text.secondary', 'fontSize': '0.8rem', '&:hover': {color: 'text.primary'}}}>
                  Charger plus
                </Button>
              )}
            </Box>
          )}
        </List>
      )}
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={drawer.onTrue}
        sx={{
          'p': 0.75,
          '&:hover': {bgcolor: 'rgba(255, 255, 255, 0.08)'},
        }}
      >
        <Badge
          badgeContent={unreadCount || null}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.6rem',
              minWidth: 14,
              height: 14,
              padding: '0 3px',
            },
          }}
        >
          <Iconify
            icon="mdi:notifications-none"
            width={22}
            sx={{color: drawer.value ? 'primary.light' : 'rgba(255, 255, 255, 0.7)'}}
          />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(4px)',
              backgroundColor: 'rgba(22, 28, 36, 0.2)',
            },
          },
        }}
        PaperProps={{
          sx: {
            width: 1,
            maxWidth: 420,
            display: 'flex',
            flexDirection: 'column',
            borderTopLeftRadius: 20,
            borderBottomLeftRadius: 20,
            overflow: 'hidden',
            boxShadow: '-20px 0 60px -8px rgba(145, 158, 171, 0.28), -4px 0 20px -4px rgba(145, 158, 171, 0.18)',
          },
        }}
      >
        {renderHead}
        {renderTabs}
        {renderList}
      </Drawer>
    </>
  );
}
